<template>
  <div v-if="visible" class="file-viewer-modal" @click="closeViewer">
    <div class="modal-content" @click.stop>
      <div class="modal-header">
        <h3 class="modal-title">{{ currentFile?.fileName || currentFile?.name }}</h3>
        <button @click="closeViewer" class="close-button">
          <i class="fas fa-times"></i>
        </button>
      </div>
      
      <div class="modal-body" ref="modalBody">
        <!-- 图片预览 -->
        <div v-if="isImage" class="image-container" ref="imageContainer" 
             @wheel="handleWheel" @mousedown="handleMouseDown" 
             @mousemove="handleMouseMove" @mouseup="handleMouseUp" 
             @mouseleave="handleMouseUp">
          <img 
            :src="fileUrl" 
            class="preview-image" 
            ref="previewImage"
            :style="imageStyle"
            @dragstart.prevent
            @load="onImageLoad"
          />
        </div>
        
        <!-- PDF预览 -->
        <div v-else-if="isPdf" class="pdf-container">
          <iframe 
            :src="fileUrl" 
            class="pdf-iframe"
            frameborder="0"
          ></iframe>
        </div>
        
        <!-- Word/Excel预览 -->
        <div v-else-if="isOfficeFile" class="office-container">
          <iframe 
            :src="getOfficePreviewUrl()" 
            class="office-iframe"
            frameborder="0"
          ></iframe>
        </div>
        
        <!-- 其他文件类型 -->
        <div v-else class="unsupported-container">
          <div class="unsupported-content">
            <i class="fas fa-file text-6xl text-gray-400 mb-4"></i>
            <p class="text-lg text-gray-600 mb-2">无法预览此文件类型</p>
            <p class="text-sm text-gray-500 mb-4">{{ currentFile?.fileName || currentFile?.name }}</p>
            <button @click="downloadFile" class="download-button">
              <i class="fas fa-download mr-2"></i>下载文件
            </button>
          </div>
        </div>
      </div>
      
      <div class="modal-footer">
        <!-- 图片缩放控制 -->
        <div v-if="isImage" class="zoom-controls">
          <button @click="zoomOut" class="zoom-button" :disabled="scale <= 0.1">
            <i class="fas fa-search-minus"></i>
          </button>
          <span class="zoom-text">{{ Math.round(scale * 100) }}%</span>
          <button @click="zoomIn" class="zoom-button" :disabled="scale >= 5">
            <i class="fas fa-search-plus"></i>
          </button>
          <button @click="resetZoom" class="zoom-button">
            <i class="fas fa-expand-arrows-alt"></i>
          </button>
        </div>
        
        <div class="action-buttons">
          <button @click="downloadFile" class="download-button">
            <i class="fas fa-download mr-2"></i>下载
          </button>
          <button @click="closeViewer" class="close-modal-button">
            关闭
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, defineProps, defineEmits, watch } from 'vue'
import { toast } from '@/utils/toast.js';

const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },
  file: {
    type: Object,
    default: null
  }
})

const emit = defineEmits(['close'])

// 响应式数据
const modalBody = ref(null)
const imageContainer = ref(null)
const previewImage = ref(null)

// 图片缩放和拖动相关
const scale = ref(1)
const translateX = ref(0)
const translateY = ref(0)
const isDragging = ref(false)
const dragStartX = ref(0)
const dragStartY = ref(0)
const lastTranslateX = ref(0)
const lastTranslateY = ref(0)

// 计算属性
const currentFile = computed(() => props.file)

const fileUrl = computed(() => {
  if (!currentFile.value) return ''
  return currentFile.value.url || currentFile.value.serverData?.url || ''
})

const fileName = computed(() => {
  if (!currentFile.value) return ''
  return currentFile.value.fileName || currentFile.value.name || ''
})

const fileExtension = computed(() => {
  if (!fileName.value) return ''
  return fileName.value.toLowerCase().split('.').pop() || ''
})

const isImage = computed(() => {
  const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp']
  return imageExtensions.includes(fileExtension.value)
})

const isPdf = computed(() => {
  return fileExtension.value === 'pdf'
})

const isOfficeFile = computed(() => {
  const officeExtensions = ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx']
  return officeExtensions.includes(fileExtension.value)
})

// 图片样式
const imageStyle = computed(() => ({
  transform: `scale(${scale.value}) translate(${translateX.value}px, ${translateY.value}px)`,
  cursor: isDragging.value ? 'grabbing' : 'grab',
  transition: isDragging.value ? 'none' : 'transform 0.2s ease'
}))

// 方法
const closeViewer = () => {
  emit('close')
  resetTransform()
}

const resetTransform = () => {
  scale.value = 1
  translateX.value = 0
  translateY.value = 0
  isDragging.value = false
  lastTranslateX.value = 0
  lastTranslateY.value = 0
}

// 图片加载完成
const onImageLoad = () => {
  resetTransform()
}

// 滚轮缩放
const handleWheel = (event) => {
  if (!isImage.value) return
  event.preventDefault()
  const delta = event.deltaY > 0 ? -0.1 : 0.1
  const newScale = Math.max(0.1, Math.min(5, scale.value + delta))
  scale.value = newScale
}

// 鼠标拖动
const handleMouseDown = (event) => {
  if (!isImage.value || scale.value <= 1) return
  isDragging.value = true
  dragStartX.value = event.clientX
  dragStartY.value = event.clientY
  lastTranslateX.value = translateX.value
  lastTranslateY.value = translateY.value
}

const handleMouseMove = (event) => {
  if (!isDragging.value || !isImage.value) return
  const deltaX = event.clientX - dragStartX.value
  const deltaY = event.clientY - dragStartY.value
  translateX.value = lastTranslateX.value + deltaX
  translateY.value = lastTranslateY.value + deltaY
}

const handleMouseUp = () => {
  isDragging.value = false
}

// 缩放控制
const zoomIn = () => {
  if (scale.value < 5) {
    scale.value = Math.min(5, scale.value + 0.2)
  }
}

const zoomOut = () => {
  if (scale.value > 0.1) {
    scale.value = Math.max(0.1, scale.value - 0.2)
  }
}

const resetZoom = () => {
  resetTransform()
}

// Office文件预览URL
const getOfficePreviewUrl = () => {
  if (!fileUrl.value) return ''
  // 使用Microsoft Office Online预览服务
  return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(fileUrl.value)}`
}

// 下载文件
const downloadFile = () => {
  if (!fileUrl.value) {
    toast.error('下载链接不存在')
    return
  }

  try {
    const link = document.createElement('a')
    link.href = fileUrl.value
    link.download = fileName.value || 'download'
    link.target = '_blank'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    toast.success('开始下载文件')
  } catch (error) {
    console.error('下载文件失败:', error)
    toast.error('下载文件失败')
  }
}

// 监听文件变化，重置状态
watch(() => props.file, () => {
  resetTransform()
})

// 监听显示状态变化
watch(() => props.visible, (newVal) => {
  if (newVal) {
    resetTransform()
  }
})
</script>

<style scoped>
/* 模态框样式 */
.file-viewer-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
}

.modal-content {
  background-color: white;
  border-radius: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: none;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  background-color: #f9fafb;
}

.modal-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #374151;
  margin: 0;
  max-width: 80%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.close-button {
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  font-size: 1.25rem;
  padding: 0.25rem;
  transition: color 0.2s;
  border-radius: 4px;
}

.close-button:hover {
  color: #ef4444;
  background-color: #fee2e2;
}

.modal-body {
  flex-grow: 1;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
}

/* 图片容器 */
.image-container {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  position: relative;
  user-select: none;
}

.preview-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  transform-origin: center;
  user-select: none;
  pointer-events: auto;
}

/* PDF容器 */
.pdf-container {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.pdf-iframe {
  width: 100%;
  height: 100%;
  border: none;
}

/* Office文件容器 */
.office-container {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.office-iframe {
  width: 100%;
  height: 100%;
  border: none;
}

/* 不支持的文件类型 */
.unsupported-container {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.unsupported-content {
  text-align: center;
  padding: 2rem;
}

/* 底部工具栏 */
.modal-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  border-top: 1px solid #e5e7eb;
  background-color: #f9fafb;
}

/* 缩放控制 */
.zoom-controls {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.zoom-button {
  background-color: #f3f4f6;
  border: 1px solid #d1d5db;
  color: #374151;
  padding: 0.5rem;
  border-radius: 0.375rem;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
}

.zoom-button:hover:not(:disabled) {
  background-color: #e5e7eb;
  border-color: #9ca3af;
}

.zoom-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.zoom-text {
  font-size: 0.875rem;
  color: #6b7280;
  min-width: 3rem;
  text-align: center;
}

/* 操作按钮 */
.action-buttons {
  display: flex;
  gap: 0.5rem;
}

.download-button {
  background-color: #10b981;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: background-color 0.2s;
}

.download-button:hover {
  background-color: #059669;
}

.close-modal-button {
  background-color: #6b7280;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
}

.close-modal-button:hover {
  background-color: #4b5563;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .modal-content {
    width: 100%;
    height: 100%;
  }
  
  .modal-header {
    padding: 0.75rem 1rem;
  }
  
  .modal-footer {
    padding: 0.75rem 1rem;
    flex-direction: column;
    gap: 0.75rem;
  }
  
  .zoom-controls {
    order: 2;
  }
  
  .action-buttons {
    order: 1;
  }
}
</style>